import type { OrderMode, PausableChannel, RestaurantIdentity } from '@/types';

const CHANNELS: PausableChannel[] = ['take_out', 'delivery', 'dine_in', 'reservation'];

export function coercePausedChannels(raw: unknown): PausableChannel[] {
  return Array.isArray(raw) ? CHANNELS.filter((channel) => raw.includes(channel)) : [];
}

type PauseState = Pick<RestaurantIdentity, 'onlineOrderingEnabled' | 'onlineOrderingPausedChannels'>;

/**
 * SC-1434 — la pause « Site ouvert » se choisit par canal. L'interrupteur
 * maître à faux sans liste est la pause complète d'avant : tout est coupé.
 * Même règle que sale_api `ordering_channel_paused` et le hub
 * `Restaurant.paused_channels`.
 */
export function isChannelPaused(identity: PauseState, channel: PausableChannel): boolean {
  // Seul un `false` explicite met en pause : un drapeau absent ne doit pas
  // fermer un restaurant ouvert.
  if (identity.onlineOrderingEnabled !== false) return false;
  const paused = identity.onlineOrderingPausedChannels ?? [];
  return paused.length === 0 || paused.includes(channel);
}

export function channelOfMode(mode: OrderMode): PausableChannel {
  if (mode === 'delivery') return 'delivery';
  return mode === 'takeaway' ? 'take_out' : 'dine_in';
}

/**
 * La commande en ligne est fermée : tous les modes que le restaurant propose
 * sont en pause. `offered` absent ou vide (configuration pas encore lue) :
 * l'emporter et la livraison.
 */
export function isOnlineOrderingPaused(identity: PauseState, offered?: OrderMode[]): boolean {
  const online = (offered ?? []).filter((mode) => mode !== 'dine_in');
  const modes: OrderMode[] = online.length > 0 ? online : ['takeaway', 'delivery'];
  return modes.every((mode) => isChannelPaused(identity, channelOfMode(mode)));
}
